import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { NavController, Platform } from '@ionic/angular';
import { NetworkConnectionService } from '@app/core/services/network-connection';
import { RouteSnapshotHistoryService } from '@app/core/services';
import { VersionConfigService } from '@app/core/services/version-config';
import { timer } from 'rxjs';
import { filter, skipWhile } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import { PushNotificationsService } from '@app/core/services/push-notifications/push-notifications.service';
import { saveCallingRoute } from '@app/core/services/router/store';
import { ChannelService } from '@app/modules/features/modules/communication/services/channel.service';
import { lostNetworkConnection } from '@app/store/auth/auth.actions';
import { filterTruthy } from '@app/core/utils/rxjs.util';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

const IOS_TYPE_KEY = 'gcm.notification.type';
const IOS_CHANNEL_KEY = 'gcm.notification.channel';
const ANDROID_TYPE_KEY = 'type';
const ANDROID_CHANNEL_KEY = 'channel';

@Injectable({
  providedIn: 'root',
})
export class ServicesOrchestratorService {
  private readonly _className = this.constructor.name;

  constructor(
    private _networkConnectionService: NetworkConnectionService,
    private _routeSnapshotHistoryService: RouteSnapshotHistoryService,
    private _versionConfigService: VersionConfigService,
    private _featureFlagService: FeatureFlagService,
    private _navController: NavController,
    private _location: Location,
    private _store: Store<State>,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private pushNotificationService: PushNotificationsService,
    private _platform: Platform,
    private _ngZone: NgZone,
    private _channelService: ChannelService,
    @Inject(DOCUMENT) private _document: Document,
  ) {}

  start(): void {
    this._listenForApplicationVersionUpdates();
    this._listenForApplicationVersionConfigs();
    this._listenForNetworkConnectionActivity();
    this._listenForRouteSnapshotHistory();
    this._listenForPushNotificationTap();
    this._listenForMaintenance();
    this._channelService.initialize();
  }

  private _listenForNetworkConnectionActivity(): void {
    this._networkConnectionService
      .getNetworkConnection()
      .pipe(
        filterTruthy(),
        filter((networkConnection) =>
          ['offline', 'none'].includes(networkConnection.type),
        ),
      )
      .subscribe(() => {
        this._store.dispatch(lostNetworkConnection());
      });
  }

  private _listenForRouteSnapshotHistory(): void {
    this._routeSnapshotHistoryService.start();
  }

  private _listenForApplicationVersionConfigs() {
    const pollingTime = 5000;
    this._ngZone.runOutsideAngular(() => {
      timer(0, pollingTime).subscribe(() => {
        this._ngZone.run(() => {
          this._versionConfigService.fetchVersionConfig().subscribe();
        });
      });
    });
  }

  private _listenForApplicationVersionUpdates() {
    let didNeedUpdating: boolean | null = null;
    this._versionConfigService
      .getAppVersionUpdateRequirementStatus()
      .pipe(skipWhile((val) => val == null))
      .subscribe((doesAppNeedUpdating: boolean | null) => {
        if (doesAppNeedUpdating) {
          this._navController.navigateForward(['update']);
        } else if (didNeedUpdating && !doesAppNeedUpdating) {
          this._location.back();
        }
        didNeedUpdating = doesAppNeedUpdating;
      });
  }

  private _listenForPushNotificationTap() {
    if (this._platform.is('capacitor')) {
      this.pushNotificationService.actionPerformed$.subscribe(
        (actionPerformed) => {
          console.log(
            'pushNotificationActionPerformed: ' +
              JSON.stringify(actionPerformed),
          );

          const isIos = this._platform.is('ios');
          const unauthenticatedRoutes = [
            'signin',
            'signout',
            'update',
            'privacy',
            'tou',
            'restricted',
            'reset',
            'confirmation',
          ];
          const url = this._document.URL;
          const NOTIFICATION_TYPE_KEY = isIos ? IOS_TYPE_KEY : ANDROID_TYPE_KEY;
          const NOTIFICATION_CHANNEL_KEY = isIos
            ? IOS_CHANNEL_KEY
            : ANDROID_CHANNEL_KEY;

          if (actionPerformed.actionId === 'tap') {
            const type: string =
              actionPerformed.notification.data[NOTIFICATION_TYPE_KEY];
            if (
              type === 'CHAT' ||
              type === 'ANNOUNCEMENT' ||
              type === 'SHIFT_REQUEST'
            ) {
              let path: string = '';
              let queryParams: Record<string, any> = {};
              if (type === 'CHAT') {
                path = 'app/communication/messages/view-chat';
                queryParams = {
                  ch: actionPerformed.notification.data[
                    NOTIFICATION_CHANNEL_KEY
                  ],
                };
              } else if (type === 'ANNOUNCEMENT') {
                path = 'app/communication/announcements/view-announcement';
                queryParams = {
                  ch: actionPerformed.notification.data[
                    NOTIFICATION_CHANNEL_KEY
                  ],
                };
              } else if (type === 'SHIFT_REQUEST') {
                path = 'app/shift-swap-requests';
                queryParams = {
                  entId: actionPerformed.notification.data.entId,
                };
              }
              const route = { url: path, queryParams };
              console.log('listenForPushNotificationTap route: ', route);
              if (
                unauthenticatedRoutes.some((unAuthedRoute) =>
                  url.includes(unAuthedRoute),
                )
              ) {
                console.log(
                  this._className,
                  `Notification opened unauthenticated page.`,
                );
                this._store.dispatch(saveCallingRoute(route));
              } else {
                console.log(
                  this._className,
                  `Notification opened authenticated page.`,
                );
                this._ngZone.run(() => {
                  this._navController.navigateRoot([route.url], {
                    animated: true,
                    animationDirection: 'forward',
                    queryParams: route.queryParams,
                  });
                });
              }
            }
          }
        },
      );
    }
  }

  private _listenForMaintenance() {
    let wasInMaintenance: boolean | null = null;
    this._featureFlagService.isInMaintenance$.subscribe((isInMaintenance) => {
      if (isInMaintenance) {
        this._navController.navigateRoot(['maintenance']);
      } else if (wasInMaintenance && !isInMaintenance) {
        this._navController.navigateRoot(['app']);
      }
      wasInMaintenance = isInMaintenance;
    });
  }
}
